import { useMemo } from 'react';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import FilterChips from 'components/shared/FilterChips';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import { TabType } from 'components/Tabs';
import { PREDECESSOR_GROUP } from 'constants/filterKeysConstants';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import { useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';

import { filtersForGroupType } from '../filters';

import BusinessDevelopmentFilterBar from './filterBar/BusinessDevelopmentFilterBar';
import BusinessDevelopmentSlideout from './slideout/BusinessDevelopmentSlideout';
import { useBusinessDevelopmentContext } from './BusinessDevelopmentContainer';
import BusinessDevelopmentDashboard from './BusinessDevelopmentDashboard';

export default function BusinessDevelopmentPage() {
  const { groupTypes, onTabChange } = useBusinessDevelopmentContext();
  const selectedGroupType = useBusinessDevelopmentStore((state) => state.selectedGroupType);
  const filters = useBusinessDevelopmentStore((state) => state.filters);

  const tabs = useMemo<TabType[]>(
    () =>
      groupTypes
        .filter((gt) => gt.id)
        .map((gt) => ({
          label: gt.displayName,
          value: gt.id,
        })),
    [groupTypes]
  );

  const selectedTab = useMemo(() => tabs.find((t) => t.value === selectedGroupType?.id), [tabs, selectedGroupType]);

  const activeFilters = useMemo(
    () => (selectedGroupType ? filtersForGroupType(filters, selectedGroupType, PREDECESSOR_GROUP) : filters),
    [filters, selectedGroupType]
  );

  const chippableFilters = Object.entries(activeFilters).reduce((acc, [key, value]) => {
    if (value instanceof FilterValueArray && value.length > 0) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});

  const handleTabClick = (tab: TabType) => {
    const match = groupTypes.find((gt) => gt.id === tab.value);
    if (match) {
      onTabChange(match);
    }
  };

  return (
    <Page>
      <StyledPageHeader>
        <BusinessDevelopmentFilterBar tabs={tabs} selectedTab={selectedTab} onTabClick={handleTabClick} />
      </StyledPageHeader>
      <Page.SubHeader>
        <DesktopOnly>
          <FilterChips filters={chippableFilters} useStore={useBusinessDevelopmentStore} />
        </DesktopOnly>
      </Page.SubHeader>
      <Page.Content>
        <BusinessDevelopmentDashboard />
      </Page.Content>
      <BusinessDevelopmentSlideout />
    </Page>
  );
}

const StyledPageHeader = styled(Page.Header)`
  background: none;
  border-bottom: none;
`;
